/**
 * NotificationBell — cloche 🔔 affichant les broadcasts ADMIN-VITAE™.
 * Poll toutes les 60 s pour un utilisateur authentifié.
 */
import React, { useEffect, useState } from "react";
import { Bell, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { fetchMyNotifications, markNotifRead } from "@/lib/admin-api";
import { api } from "@/lib/api";

const fmt = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return new Date(iso).toLocaleDateString("fr-FR");
};

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  async function load() {
    try {
      const r = await fetchMyNotifications();
      setItems(r?.data || []);
      setUnread(r?.unread || 0);
    } catch { /* silent — not logged or no endpoint yet */ }
  }
  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  async function readOne(id) {
    try { await markNotifRead(id); load(); } catch { /* silent */ }
  }
  async function readAll() {
    try { await api.post("/notifications/read-all"); load(); } catch { /* silent */ }
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="relative flex h-9 w-9 items-center justify-center rounded-full hover:bg-accent/60"
          aria-label="Notifications"
          data-testid="notif-bell"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[9px] font-bold text-white"
              data-testid="notif-bell-count">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80" data-testid="notif-dropdown">
        <div className="flex items-center justify-between px-2">
          <DropdownMenuLabel className="px-0 py-1">
            Notifications {unread > 0 && <span className="ml-1 rounded bg-red-600 px-1.5 py-0.5 text-[9px] font-bold text-white">{unread}</span>}
          </DropdownMenuLabel>
          {unread > 0 && (
            <button onClick={readAll} className="text-[10px] text-muted-foreground hover:text-foreground" data-testid="notif-read-all">
              Tout marquer lu
            </button>
          )}
        </div>
        <DropdownMenuSeparator />
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-6 text-center text-xs text-muted-foreground">Aucune notification.</div>
          ) : (
            items.map((n) => (
              <div key={n.id}
                className={`group border-b p-3 text-sm last:border-0 ${n.is_read ? "opacity-60" : "bg-red-50/30 dark:bg-red-950/10"}`}
                data-testid={`notif-${n.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="font-semibold">{n.title}</div>
                    <div className="mt-0.5 whitespace-pre-wrap text-xs text-muted-foreground">{n.message}</div>
                    <div className="mt-1 text-[10px] text-muted-foreground">{fmt(n.created_at)}</div>
                  </div>
                  {!n.is_read && (
                    <button onClick={() => readOne(n.id)} title="Marquer comme lu"
                      className="opacity-0 transition group-hover:opacity-100" data-testid={`notif-read-${n.id}`}>
                      <Check className="h-4 w-4 text-muted-foreground hover:text-emerald-600" />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
